import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import FeatherIcon from "feather-icons-react";

import styles from "./AttachmentImage.module.scss";

/**
 * The picture at full size over everything, the assistant's own window included — which is itself
 * portalled to the body at z-index 1300, so this goes to the body too and sits above it. Escape, the
 * cross and a click anywhere off the picture all close it.
 *
 * Its events are stopped at its root. A portal moves the DOM, not the React tree, so a mousedown
 * here still bubbles to the palette's overlay — whose own mousedown closes the whole window — and a
 * click to whatever holds the question.
 */
const Lightbox = ({ name, src, onClose }) => {
    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key !== "Escape") return;
            /* The palette closes on Escape as well, unless something has already used the press —
               the same rule the archive's row menu goes by. This one is for the picture only. */
            e.preventDefault();
            onClose();
        };
        document.addEventListener("keydown", onKeyDown, true);
        return () => document.removeEventListener("keydown", onKeyDown, true);
    }, [onClose]);

    const stop = (e) => e.stopPropagation();

    return ReactDOM.createPortal(
        <div
            className={styles.overlay}
            onMouseDown={stop}
            onClick={(e) => {
                stop(e);
                onClose();
            }}
            role="dialog"
            aria-modal="true"
            aria-label={name}
        >
            <button
                type="button"
                className={styles.close}
                onClick={(e) => {
                    stop(e);
                    onClose();
                }}
                aria-label="Close"
            >
                <FeatherIcon icon="x" size={20} />
            </button>
            <figure className={styles.figure} onClick={stop}>
                <img src={src} alt={name} className={styles.full} />
                <figcaption className={styles.caption}>{name}</figcaption>
            </figure>
        </div>,
        document.body
    );
};

/**
 * A sent image as what it is, a picture, rather than a file name: a thumbnail in the question that
 * opens full size on a click. Only an image this page still holds comes here — see
 * assistantSessionImages — and anything else, a PDF or an image from before a reload, keeps its chip.
 */
const AttachmentImage = ({ name, src }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <>
            <button
                type="button"
                className={styles.thumb}
                onClick={() => setIsOpen(true)}
                title={name}
                aria-label={`Open ${name}`}
            >
                <img src={src} alt={name} />
            </button>
            {isOpen && <Lightbox name={name} src={src} onClose={() => setIsOpen(false)} />}
        </>
    );
};

export default AttachmentImage;
